import { useState, useRef, useEffect } from "react";
import {
  AlertBanner,
  Button,
  ButtonIcon,
  Chip,
  Icon,
  Typography,
} from "@saviynt/design-system";
import { useNavigate } from "@tanstack/react-router";
import { FormattedMessage, useIntl } from "react-intl";
import type { SsoConfigResponse } from "@repo/api";
import { useRefreshMetadata, useUpdateSso } from "../hooks";
import PreCheckDialog from "./PreCheckDialog";
import "./SsoListPage.css";

interface SsoListPageProps {
  config: SsoConfigResponse | SsoConfigResponse[];
  successMessage?: string | null;
}

export default function SsoListPage({
  config,
  successMessage,
}: SsoListPageProps) {
  const navigate = useNavigate();
  const intl = useIntl();
  const isMultiple = Array.isArray(config);
  const providers = isMultiple ? config : [config];

  const refreshMetadata = useRefreshMetadata();
  const updateSso = useUpdateSso();

  const [showPreCheck, setShowPreCheck] = useState(false);
  const [kebabOpenIndex, setKebabOpenIndex] = useState<number | null>(null);
  const [isSuccessDismissed, setIsSuccessDismissed] = useState(false);
  const kebabRef = useRef<HTMLDivElement>(null);

  // Clear search param from URL after displaying success banner
  useEffect(() => {
    if (successMessage) {
      navigate({ search: true, replace: true });
    }
  }, [successMessage, navigate]);

  // Close kebab on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (kebabRef.current && !kebabRef.current.contains(e.target as Node)) {
        setKebabOpenIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleRowClick = () => {
    navigate({ to: "/admin/settings/sso-settings/edit" });
  };

  const handleAddProvider = () => {
    navigate({ to: "/admin/settings/sso-settings/create" });
  };

  const handleRefreshMetadata = () => {
    refreshMetadata.mutate();
    setKebabOpenIndex(null);
  };

  const handleToggleEnabled = (currentStatus: string) => {
    setKebabOpenIndex(null);
    if (currentStatus === "ENABLED") {
      updateSso.mutate({ status: "DISABLED" });
    } else {
      setShowPreCheck(true);
    }
  };

  const handleSetPilot = () => {
    setKebabOpenIndex(null);
    updateSso.mutate({ status: "TEST" });
  };

  const handleDisable = () => {
    setKebabOpenIndex(null);
    updateSso.mutate({ status: "DISABLED" });
  };

  const handleConfirmEnable = () => {
    updateSso.mutate(
      { status: "ENABLED" },
      { onSuccess: () => setShowPreCheck(false) },
    );
  };

  return (
    <div className="SsoList">
      {/* Success Banner */}
      {successMessage && !isSuccessDismissed && (
        <div
          aria-live="polite"
          role="status"
          style={{ padding: "16px 16px 0" }}
        >
          <AlertBanner
            colorTheme="Success"
            title={successMessage}
            isFloating={false}
            onCancel={() => setIsSuccessDismissed(true)}
            isColonVisible={false}
          />
        </div>
      )}

      {/* Page Header */}
      <div className="SsoList__pageHeader">
        <div className="SsoList__pageHeaderContent">
          <Typography kind="h1" color="--color-foreground-neutral-bold">
            <FormattedMessage
              id="YZaqGu"
              defaultMessage="SSO Providers"
              description="SSO list page heading"
            />
          </Typography>
        </div>
        <div className="SsoList__tabs">
          <div className="SsoList__tabItem SsoList__tabItem--active">
            <Icon
              kind="UserManagement"
              size="small"
              color="--color-foreground-secondary-bold"
            />
            <Typography kind="h3" color="--color-foreground-secondary-bold">
              <FormattedMessage
                id="LJ8ypG"
                defaultMessage="Providers"
                description="Providers tab label"
              />
            </Typography>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="SsoList__content">
        {/* Add Provider (only when multiple configs supported) */}
        {isMultiple && (
          <div className="SsoList__toolbar">
            <Button
              kind="outlined"
              size="medium"
              onClick={handleAddProvider}
              leftIcon={<Icon kind="Add" size="small" />}
            >
              <FormattedMessage
                id="tw2/Xp"
                defaultMessage="Add Provider"
                description="Add provider button label"
              />
            </Button>
          </div>
        )}

        {/* Provider List */}
        {providers.map((provider, index) => (
          <div key={provider.displayName ?? index} className="SsoList__row">
            <div
              className="SsoList__rowClickArea"
              onClick={handleRowClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleRowClick();
              }}
            >
              <div className="SsoList__rowIcon">
                <Icon
                  kind="Entitlement"
                  size="medium"
                  color="--color-foreground-neutral-bold"
                />
              </div>
              <div className="SsoList__rowContent">
                <Typography
                  kind="body3"
                  color="--color-foreground-secondary-medium"
                >
                  <FormattedMessage
                    id="IIA4Kq"
                    defaultMessage="SAML"
                    description="SAML protocol label in list row"
                  />
                </Typography>
                <Typography kind="h3" color="--color-foreground-neutral-bold">
                  {provider.displayName ??
                    provider.idpName ??
                    intl.formatMessage({
                      id: "JsiFXa",
                      defaultMessage: "N/A",
                      description: "Placeholder for missing value",
                    })}
                </Typography>
              </div>
              <div className="SsoList__rowStatus">
                <Chip
                  label={
                    provider.status === "ENABLED"
                      ? intl.formatMessage({
                          id: "nSBRya",
                          defaultMessage: "Active",
                          description: "SSO active status",
                        })
                      : provider.status === "TEST"
                        ? intl.formatMessage({
                            id: "GSPuuh",
                            defaultMessage: "Pilot",
                            description: "SSO pilot status",
                          })
                        : intl.formatMessage({
                            id: "zYsKBl",
                            defaultMessage: "Disabled",
                            description: "SSO disabled status",
                          })
                  }
                  color={
                    provider.status === "ENABLED"
                      ? "success"
                      : provider.status === "TEST"
                        ? "warning"
                        : "neutral"
                  }
                  size="medium"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="SsoList__rowActions">
              <ButtonIcon
                kind="outlinedNeutral"
                size="medium"
                icon={<Icon kind="Edit" size="small" />}
                ariaLabel={intl.formatMessage({
                  id: "hgZHFw",
                  defaultMessage: "Edit provider",
                  description: "Edit provider button",
                })}
                onClick={handleRowClick}
              />
              <div className="SsoList__kebabWrapper" ref={kebabRef}>
                <ButtonIcon
                  kind="ghost"
                  size="small"
                  icon={<Icon kind="MoreVertical" size="small" />}
                  ariaLabel={intl.formatMessage({
                    id: "FRzsUK",
                    defaultMessage: "More actions",
                    description: "Kebab menu button",
                  })}
                  onClick={() =>
                    setKebabOpenIndex(kebabOpenIndex === index ? null : index)
                  }
                />
                {kebabOpenIndex === index && (
                  <div
                    className="SsoList__kebabMenu"
                    role="menu"
                    aria-label={intl.formatMessage({
                      id: "8uBgBX",
                      defaultMessage: "Provider actions",
                      description: "Kebab menu aria label",
                    })}
                  >
                    {provider.status !== "ENABLED" && (
                      <button
                        type="button"
                        className="SsoList__kebabMenuItem"
                        role="menuitem"
                        onClick={() => handleToggleEnabled(provider.status)}
                      >
                        <Typography
                          kind="body2"
                          color="--color-foreground-secondary-bold"
                        >
                          <FormattedMessage
                            id="BoH3Y7"
                            defaultMessage="Enable"
                            description="Enable SSO menu item"
                          />
                        </Typography>
                      </button>
                    )}
                    {provider.status !== "TEST" && (
                      <button
                        type="button"
                        className="SsoList__kebabMenuItem"
                        role="menuitem"
                        onClick={handleSetPilot}
                      >
                        <Typography
                          kind="body2"
                          color="--color-foreground-secondary-bold"
                        >
                          <FormattedMessage
                            id="q6ZZLc"
                            defaultMessage="Pilot"
                            description="Set SSO to pilot mode menu item"
                          />
                        </Typography>
                      </button>
                    )}
                    {provider.status !== "DISABLED" && (
                      <button
                        type="button"
                        className="SsoList__kebabMenuItem"
                        role="menuitem"
                        onClick={handleDisable}
                      >
                        <Typography
                          kind="body2"
                          color="--color-foreground-secondary-bold"
                        >
                          <FormattedMessage
                            id="Ivb1Ls"
                            defaultMessage="Disable"
                            description="Disable SSO menu item"
                          />
                        </Typography>
                      </button>
                    )}
                    <button
                      type="button"
                      className="SsoList__kebabMenuItem"
                      role="menuitem"
                      onClick={handleRefreshMetadata}
                    >
                      <Typography
                        kind="body2"
                        color="--color-foreground-secondary-bold"
                      >
                        <FormattedMessage
                          id="2WeLqi"
                          defaultMessage="Refresh Certificate"
                          description="Refresh certificate menu item"
                        />
                      </Typography>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <PreCheckDialog
        isOpen={showPreCheck}
        onConfirm={handleConfirmEnable}
        onCancel={() => setShowPreCheck(false)}
        isPending={updateSso.isPending}
      />
    </div>
  );
}
